import React, { useState } from 'react';

interface MiDashAnimatedLogomarkProps {
  size?: number;
  className?: string;
  forceAnimation?: boolean; // Force animation to be active (e.g., when AI is typing)
  variant?: 'full' | 'icon';
  theme?: 'dark' | 'light';
  withText?: boolean; // Render dots + "MiDash" text in single SVG (matches PNG exactly)
}

// PNG-matching dot positions (viewBox 0 0 380 100)
const TEXT_DOTS: { cx: number; cy: number; type: 'lit' | 'dim'; delay: string }[] = [
  // Row 1
  { cx: 8, cy: 35.25, type: 'lit', delay: '0s' },
  { cx: 19.5, cy: 35.25, type: 'dim', delay: '0.15s' },
  { cx: 31, cy: 35.25, type: 'lit', delay: '0.3s' },
  { cx: 42.5, cy: 35.25, type: 'dim', delay: '0.45s' },
  // Row 2
  { cx: 8, cy: 46.75, type: 'dim', delay: '1.65s' },
  { cx: 42.5, cy: 46.75, type: 'lit', delay: '0.6s' },
  // Row 3
  { cx: 8, cy: 58.25, type: 'lit', delay: '1.5s' },
  { cx: 42.5, cy: 58.25, type: 'dim', delay: '0.75s' },
  // Row 4
  { cx: 8, cy: 69.75, type: 'dim', delay: '1.35s' },
  { cx: 19.5, cy: 69.75, type: 'lit', delay: '1.2s' },
  { cx: 31, cy: 69.75, type: 'dim', delay: '1.05s' },
  { cx: 42.5, cy: 69.75, type: 'lit', delay: '0.9s' },
];

const MiDashAnimatedLogomark: React.FC<MiDashAnimatedLogomarkProps> = ({
  size = 32,
  className = '',
  forceAnimation = false,
  variant = 'full',
  theme = 'dark',
  withText = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Animation should be active if hovered OR forceAnimation is true
  const isAnimating = isHovered || forceAnimation;

  const isDark = theme === 'dark';

  // Gradient colors: white spheres on dark bg, dark spheres on light bg
  const lit = isDark
    ? { s0: '#ffffff', s30: '#ffffff', s70: '#e8ecf0', s100: '#cbd5e1' }
    : { s0: '#6b7280', s30: '#4b5563', s70: '#374151', s100: '#1f2937' };
  const dim = isDark
    ? { s0: '#ffffff', s30: '#f8f9fa', s70: '#e8ecf0', s100: '#cbd5e1', o: [0.35, 0.3, 0.25, 0.2] }
    : { s0: '#6b7280', s30: '#4b5563', s70: '#374151', s100: '#1f2937', o: [0.35, 0.3, 0.25, 0.2] };
  const ctr = isDark
    ? { s0: '#ffffff', s25: '#ffffff', s65: '#e8ecf0', s100: '#cbd5e1' }
    : { s0: '#6b7280', s25: '#4b5563', s65: '#374151', s100: '#1f2937' };

  const uid = withText ? `logo-${isDark ? 'w' : 'd'}` : `dot-${isDark ? 'w' : 'd'}-${size}`;

  const gradientDefs = (
    <defs>
      <radialGradient id={`${uid}-lit`} cx="30%" cy="30%">
        <stop offset="0%" stopColor={lit.s0} stopOpacity="1" />
        <stop offset="30%" stopColor={lit.s30} stopOpacity="1" />
        <stop offset="70%" stopColor={lit.s70} stopOpacity="1" />
        <stop offset="100%" stopColor={lit.s100} stopOpacity="1" />
      </radialGradient>
      <radialGradient id={`${uid}-dim`} cx="30%" cy="30%">
        <stop offset="0%" stopColor={dim.s0} stopOpacity={dim.o[0]} />
        <stop offset="30%" stopColor={dim.s30} stopOpacity={dim.o[1]} />
        <stop offset="70%" stopColor={dim.s70} stopOpacity={dim.o[2]} />
        <stop offset="100%" stopColor={dim.s100} stopOpacity={dim.o[3]} />
      </radialGradient>
      <radialGradient id={`${uid}-ctr`} cx="28%" cy="28%">
        <stop offset="0%" stopColor={ctr.s0} stopOpacity="1" />
        <stop offset="25%" stopColor={ctr.s25} stopOpacity="1" />
        <stop offset="65%" stopColor={ctr.s65} stopOpacity="1" />
        <stop offset="100%" stopColor={ctr.s100} stopOpacity="1" />
      </radialGradient>
      {withText && (
        <filter id={`${uid}-shadow`}>
          <feDropShadow dx="0.2" dy="0.4" stdDeviation="0.4" floodColor="#000000" floodOpacity="0.12" />
        </filter>
      )}
    </defs>
  );

  // Combined dots + "MiDash" text in single SVG — matches PNG exactly
  if (withText) {
    const height = size;
    const width = size * 3.8;
    const shadowFilter = `url(#${uid}-shadow)`;

    return (
      <svg
        viewBox="0 0 380 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        className={className}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      >
        {gradientDefs}

        {/* Outer dots at PNG-exact positions */}
        {TEXT_DOTS.map((dot, i) => (
          <circle
            key={i}
            cx={dot.cx}
            cy={dot.cy}
            r={4.5}
            fill={`url(#${uid}-${dot.type})`}
            filter={shadowFilter}
          >
            {isAnimating && (
              <animate
                attributeName="opacity"
                values={dot.type === 'lit' ? '1;0.3;1' : '0.3;1;0.3'}
                dur="2s"
                begin={dot.delay}
                repeatCount="indefinite"
              />
            )}
          </circle>
        ))}

        {/* Center circle */}
        <circle cx={25.25} cy={52.5} r={7} fill={`url(#${uid}-ctr)`} filter={shadowFilter}>
          {isAnimating && (
            <>
              <animate attributeName="opacity" values="1;0.6;1" dur="2s" begin="0s" repeatCount="indefinite" />
              <animate attributeName="r" values="7;6;7" dur="2s" begin="0s" repeatCount="indefinite" />
            </>
          )}
        </circle>

        {/* MiDash text — same font, size, position as PNG source SVG */}
        <text
          x={53}
          y={74}
          fontFamily="Montserrat, sans-serif"
          fontSize={60}
          fontWeight={600}
          letterSpacing="-0.02em"
          style={{ fill: 'var(--text)' }}
        >
          MiDash
        </text>
      </svg>
    );
  }

  // Icon-only rendering (default — used in 29+ places)
  const viewBox = variant === 'icon' ? '200 200 600 600' : '0 0 1000 1000';

  return (
    <svg
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
    >
      {gradientDefs}

      <circle cx="280" cy="280" r="50" fill={`url(#${uid}-lit)`}>
        {isAnimating && <animate attributeName="opacity" values="1;0.3;1" dur="2s" begin="0s" repeatCount="indefinite" />}
      </circle>
      <circle cx="420" cy="280" r="50" fill={`url(#${uid}-dim)`}>
        {isAnimating && <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" begin="0.15s" repeatCount="indefinite" />}
      </circle>
      <circle cx="580" cy="280" r="50" fill={`url(#${uid}-lit)`}>
        {isAnimating && <animate attributeName="opacity" values="1;0.3;1" dur="2s" begin="0.3s" repeatCount="indefinite" />}
      </circle>
      <circle cx="720" cy="280" r="50" fill={`url(#${uid}-dim)`}>
        {isAnimating && <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" begin="0.45s" repeatCount="indefinite" />}
      </circle>
      <circle cx="280" cy="420" r="50" fill={`url(#${uid}-dim)`}>
        {isAnimating && <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" begin="1.65s" repeatCount="indefinite" />}
      </circle>
      <circle cx="720" cy="420" r="50" fill={`url(#${uid}-lit)`}>
        {isAnimating && <animate attributeName="opacity" values="1;0.3;1" dur="2s" begin="0.6s" repeatCount="indefinite" />}
      </circle>
      <circle cx="280" cy="580" r="50" fill={`url(#${uid}-lit)`}>
        {isAnimating && <animate attributeName="opacity" values="1;0.3;1" dur="2s" begin="1.5s" repeatCount="indefinite" />}
      </circle>
      <circle cx="720" cy="580" r="50" fill={`url(#${uid}-dim)`}>
        {isAnimating && <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" begin="0.75s" repeatCount="indefinite" />}
      </circle>
      <circle cx="280" cy="720" r="50" fill={`url(#${uid}-dim)`}>
        {isAnimating && <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" begin="1.35s" repeatCount="indefinite" />}
      </circle>
      <circle cx="420" cy="720" r="50" fill={`url(#${uid}-lit)`}>
        {isAnimating && <animate attributeName="opacity" values="1;0.3;1" dur="2s" begin="1.2s" repeatCount="indefinite" />}
      </circle>
      <circle cx="580" cy="720" r="50" fill={`url(#${uid}-dim)`}>
        {isAnimating && <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" begin="1.05s" repeatCount="indefinite" />}
      </circle>
      <circle cx="720" cy="720" r="50" fill={`url(#${uid}-lit)`}>
        {isAnimating && <animate attributeName="opacity" values="1;0.3;1" dur="2s" begin="0.9s" repeatCount="indefinite" />}
      </circle>
      <circle cx="500" cy="500" r="75" fill={`url(#${uid}-ctr)`}>
        {isAnimating && (
          <>
            <animate attributeName="opacity" values="1;0.6;1" dur="2s" begin="0s" repeatCount="indefinite" />
            <animate attributeName="r" values="75;64;75" dur="2s" begin="0s" repeatCount="indefinite" />
          </>
        )}
      </circle>
    </svg>
  );
};

export default MiDashAnimatedLogomark;
